import { useUserContext } from '@nrdy-marketing-engine/server/src/user/context';
import { upperFirst } from '@s-libs/micro-dash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { transformMeToClient } from '../../api/user/create-account';
import { trackCartEvent, trackCartFailure } from '../../lib/track-service/track-cart';

/*
  conversion api docs

  https://github.com/varsitytutors/llt-lab-users/blob/b445794e602783a58b161eb553775d77d3c68c34/documentation/resources/convert_user.json

  description: "Converts a pt_user / mobile_app_user to a client",

  pt user and mobile app user are considered interchangable when it comes to client conversion
*/

export enum ConvertUserToClientState {
  FAIL = 'fail',
  INITIAL = 'initial',
  LOADING = 'loading',
  SUCCESS = 'success',
}

export const CLIENT_CONVERTION_ERROR_KEY = `failedToConvertUserToClient-${window.location.search}`;

function useConvertUserToClient() {
  const ALLOWED_ATTEMPTS = 2;
  const { user, loading: userLoading, convertUserAndStore } = useUserContext();
  const client = useMemo(() => transformMeToClient(user), [user]);
  const [convertUserToClientState, setConversionState] = useState<ConvertUserToClientState>(
    ConvertUserToClientState.INITIAL
  );
  const conversionErrorEmail = window.sessionStorage.getItem(CLIENT_CONVERTION_ERROR_KEY);

  const [loading, setLoading] = useState(false);
  const [userConversionAttemptedCount, setUserConversionAttemptedCount] = useState(0);
  const canAttemptUserConversion = useMemo(() => {
    const CANNOT_CONVERT = false;

    // conversion is already being attempted
    if (loading) return CANNOT_CONVERT;

    // user conversion has already been attempted more than the allowed attempts
    if (userConversionAttemptedCount >= ALLOWED_ATTEMPTS) return CANNOT_CONVERT;

    // user is currently being loaded
    if (userLoading) return CANNOT_CONVERT;

    // user isn't present in the context
    if (!user) return CANNOT_CONVERT;

    // user is already a client
    if (client?.uuid) return CANNOT_CONVERT;

    // user is not a practice test user nor mobile app user
    if (!user.entity.role.includes('pt_user') && !user.entity.role.includes('mobile_app_user'))
      return CANNOT_CONVERT;

    if (conversionErrorEmail === user?.entity?.email) return CANNOT_CONVERT;

    return true;
  }, [
    client?.uuid,
    loading,
    user,
    userConversionAttemptedCount,
    userLoading,
    conversionErrorEmail,
  ]);

  const userRoleToConvert = useMemo(() => {
    const userRole = user?.entity?.role;

    if (!userRole) return;

    return upperFirst(userRole).split('_').join(' ');
  }, [user]);

  const trackConversionSuccess = useCallback(async (data) => {
    await trackCartEvent(
      'checkout_pt_user_convert_to_client_success',
      {
        clientUuid: data?.entity?.client_uuid,
      },
      data
    );
  }, []);

  const trackConversionFailure = useCallback(
    async (err) => {
      await trackCartFailure(
        'checkout_pt_user_convert_to_client_failure',
        {
          clientUuid: user?.entity?.client_uuid,
          error: err,
        },
        err?.message
      );
    },
    [user?.entity?.client_uuid]
  );

  const conversionAttemptCleanup = useCallback(() => {
    setLoading(false);
    setUserConversionAttemptedCount((prev) => prev + 1);
  }, []);

  const saveFailedConversion = useCallback(() => {
    if (user && ['pt_user', 'mobile_app_user'].includes(user?.entity?.role)) {
      window.sessionStorage.setItem(CLIENT_CONVERTION_ERROR_KEY, user?.entity?.email);
    }
  }, [user]);

  useEffect(
    function _convertUserToClient() {
      if (!canAttemptUserConversion) return;

      setLoading(true);
      setConversionState(ConvertUserToClientState.LOADING);

      convertUserAndStore()
        .then((data) => {
          setConversionState(ConvertUserToClientState.SUCCESS);
          trackConversionSuccess(data);
        })
        .catch((err) => {
          if (userConversionAttemptedCount == ALLOWED_ATTEMPTS - 1) {
            saveFailedConversion();
            window.location.href = `/nme/logout?redirect_to=${window.location.href}`;
          }

          trackConversionFailure(err);
        })
        .finally(conversionAttemptCleanup);
    },
    [
      userConversionAttemptedCount,
      canAttemptUserConversion,
      convertUserAndStore,
      trackConversionSuccess,
      trackConversionFailure,
      conversionAttemptCleanup,
      saveFailedConversion,
    ]
  );

  useEffect(() => {
    if (conversionErrorEmail === user?.entity?.email) {
      setConversionState(ConvertUserToClientState.FAIL);
    }
  }, [user]);

  return { convertUserToClientState };
}

export default useConvertUserToClient;
